import { z } from "zod";
import { FLOWS, RISK_LEVELS } from "./enums.js";

/**
 * Contratos tipados de los resúmenes ejecutivos (tarjetas de aprobación).
 * Se validan al generarlos y al leerlos; se almacenan versionados (tabla `summaries`).
 *
 * Regla: nunca se rellena un campo con información inventada. Si un dato no está
 * disponible, el campo se deja vacío y se declara en `incompleteFields`.
 */

export const confidenceSchema = z.object({
  level: z.enum(["low", "medium", "high", "unknown"]),
  rationale: z.string().optional(),
});

export const complexitySchema = z.enum(["trivial", "small", "medium", "large", "unknown"]);

const generatedBy = z.enum(["deterministic", "llm", "agent"]);

export const planningSummarySchema = z.object({
  title: z.string(),
  problem: z.string(),
  proposedSolution: z.string(),
  affectedComponents: z.array(z.string()),
  inScope: z.array(z.string()),
  outOfScope: z.array(z.string()),
  risks: z.array(z.string()),
  acceptanceCriteria: z.array(z.string()),
  confidence: confidenceSchema,
  estimatedComplexity: complexitySchema,
  decisionRequired: z.string(),
  // metadatos de trazabilidad (derivados por el backend, no por el agente)
  riskLevel: z.enum(RISK_LEVELS),
  flow: z.enum(FLOWS),
  estimates: z.object({
    cost: z.object({ available: z.boolean(), note: z.string() }),
  }),
  incompleteFields: z.array(z.string()),
  generatedBy,
});
export type PlanningSummary = z.infer<typeof planningSummarySchema>;

export const changedFileSchema = z.object({
  path: z.string(),
  status: z.string(),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
});

export const deliverySummarySchema = z.object({
  title: z.string(),
  /** Narración del agente: NO verificada. Los hechos están en los campos siguientes. */
  implemented: z.string(),
  filesOrComponentsChanged: z.array(changedFileSchema),
  changesFromPlan: z.object({
    unplannedFiles: z.array(z.string()),
    plannedButUntouched: z.array(z.string()),
    notes: z.array(z.string()),
  }),
  testsExecuted: z.array(
    z.object({
      name: z.string(),
      status: z.string(),
      conclusion: z.string().nullable(),
      url: z.string().nullable().optional(),
    }),
  ),
  testResults: z.object({
    executed: z.boolean(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    headline: z.string(),
  }),
  knownLimitations: z.array(z.string()),
  risks: z.array(z.string()),
  pullRequest: z
    .object({
      number: z.number().int(),
      url: z.string(),
      state: z.string(),
      headSha: z.string(),
      mergeableState: z.string().nullable(),
      reviewDecision: z.string().nullable(),
    })
    .nullable(),
  usage: z.object({
    measured: z.boolean(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadTokens: z.number().int().nonnegative(),
    cacheWriteTokens: z.number().int().nonnegative(),
    totalTokens: z.number().int().nonnegative(),
    estimatedCost: z.object({ amount: z.number(), currency: z.string(), basis: z.string() }).nullable(),
    note: z.string(),
  }),
  decisionRequired: z.string(),
  /** `ready` solo si no hay incidencias; con CI fallido o sin datos nunca se presenta como lista. */
  status: z.enum(["ready", "blocked", "incomplete"]),
  issues: z.array(z.string()),
  riskLevel: z.enum(RISK_LEVELS),
  flow: z.enum(FLOWS),
  incompleteFields: z.array(z.string()),
  generatedBy,
});
export type DeliverySummary = z.infer<typeof deliverySummarySchema>;

/**
 * Formato que se le pide al agente al final de su salida de planificación.
 * Se parsea como dato NO confiable: si no valida, se cae al resumen determinista.
 */
export const agentPlanPayloadSchema = z.object({
  problem: z.string().min(1),
  proposedSolution: z.string().min(1),
  affectedComponents: z.array(z.string()).default([]),
  plannedFiles: z.array(z.string()).default([]),
  inScope: z.array(z.string()).default([]),
  outOfScope: z.array(z.string()).default([]),
  risks: z.array(z.string()).default([]),
  acceptanceCriteria: z.array(z.string()).default([]),
  confidence: z.enum(["low", "medium", "high"]).optional(),
  estimatedComplexity: z.enum(["trivial", "small", "medium", "large"]).optional(),
});
export type AgentPlanPayload = z.infer<typeof agentPlanPayloadSchema>;

/**
 * Esquema MÍNIMO que debe cumplir un plan nuevo para que el alcance sea verificable: problema, solución, criterios de
 * aceptación y ficheros previstos. `agentPlanPayloadSchema` (laxo) se conserva solo para LEER planes antiguos.
 */
export const strictPlanSchema = agentPlanPayloadSchema.extend({
  acceptanceCriteria: z.array(z.string().trim().min(1)).min(1),
  plannedFiles: z.array(z.string().trim().min(1)).min(1),
});

/** Qué le falta a un plan para cumplir el esquema mínimo (vacío = completo). `plannedFiles` son las rutas ya normalizadas y válidas. */
export function planMissingFields(payload: AgentPlanPayload | null, plannedFiles: string[]): string[] {
  const missing: string[] = [];
  if (!payload) return ["problema", "solución propuesta", "criterios de aceptación", "ficheros previstos"];
  if (!payload.problem?.trim()) missing.push("problema");
  if (!payload.proposedSolution?.trim()) missing.push("solución propuesta");
  if (!payload.acceptanceCriteria.some((c) => c.trim())) missing.push("criterios de aceptación");
  if (plannedFiles.length === 0) missing.push("ficheros previstos");
  return missing;
}
